import React, { useEffect, useMemo, useState } from 'react';
import { useApiEnvironment } from '../context/ApiEnvironmentContext';
import { getProfileImageUrl } from '../utils/profileImage';

function fallbackInitial(user) {
  const name = user?.name ?? user?.Name ?? user?.username ?? user?.Username;
  if (typeof name === 'string' && name.trim()) return name.trim()[0].toUpperCase();
  const id = user?.user_id ?? user?.UserID;
  if (id != null && id !== '') return String(id)[0];
  return '?';
}

/**
 * Loads images directly from the public S3 (or CDN base) URL in `s3_endpoint` — not via auth-admin.
 * Tries `_small` first (matches upload variants), then falls back to `original` key if that object is missing.
 *
 * @param {{ user: object, size?: number, className?: string, variant?: import('../utils/profileImage').ProfileImageVariant }} props
 */
export default function UserAvatar({ user, size = 32, className = '', variant = 'small' }) {
  const { apiEnvironment } = useApiEnvironment();
  const [fallbackStep, setFallbackStep] = useState(0);
  const url = useMemo(() => {
    if (!user) return null;
    const envOpts = { apiEnvironment, variant };
    if (fallbackStep === 0) return getProfileImageUrl(user, envOpts);
    if (variant !== 'original') return getProfileImageUrl(user, { apiEnvironment, variant: 'original' });
    return null;
  }, [user, variant, fallbackStep, apiEnvironment]);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setFallbackStep(0);
    setBroken(false);
  }, [apiEnvironment, user?.user_id, user?.UserID, user?.object_key, user?.ObjectKey]);
  const initial = fallbackInitial(user);
  const dim = { width: size, height: size };

  if (!url || broken) {
    return (
      <span className="inline-flex shrink-0 leading-none">
        <div
          className={`rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-dark-text-secondary font-medium shrink-0 ${className}`}
          style={{ ...dim, fontSize: Math.max(10, Math.round(size * 0.35)) }}
          aria-hidden
        >
          {initial}
        </div>
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 leading-none">
      <img
        src={url}
        alt=""
        className={`rounded-full object-cover border border-dark-border shrink-0 block ${className}`}
        style={dim}
        onError={() => {
          if (fallbackStep === 0 && variant !== 'original') setFallbackStep(1);
          else setBroken(true);
        }}
      />
    </span>
  );
}
